import { useThdCustomer } from '@thd-olt-functional/customer-information';
import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FeatureSwitches } from '../constants/feature-switches';
import {
  GetCPSFilters,
  GetOrderHistoryData,
} from '../hooks/purchase-history-hooks';
import { useCommonContext } from '../hooks/useContexts';
import { IAlert, TDateSelectValues } from '../models/common';
import { IOrderDetailsResponse } from '../models/order-details';
import { ILDRequest, IPHFilterRequest } from '../models/purchase-history';
import { getWindow } from '../utils/general';
import { getFilterDetailsFromSession } from '../utils/purchase-history/common';
import {
  getAccountScopedStorageKey,
  VIEW_TYPE_STORAGE_KEY,
} from '../utils/user';
import { isUserAllowedToViewAllPurchases } from '../utils/policy-utils';
import {
  initLDRequestBuilder,
  initPHRequestBuilder,
} from '../utils/purchase-history';
import {
  IPurchaseHistoryContext,
  PurchaseHistoryContext,
} from './PurchaseHistoryContext';
import { usePolicyAPI } from '../hooks/usePolicyAPI';

interface IPurchaseHistoryProvider {
  children: ReactNode;
  customerType: string;
  customerRole: string;
}

export const PurchaseHistoryProvider: FC<IPurchaseHistoryProvider> = ({
  children,
  customerType,
  customerRole,
}) => {
  const { svocID } = useThdCustomer();
  const { timezone } = useCommonContext();
  const [searchParams] = useSearchParams();
  const { rules, rulesLoaded } = usePolicyAPI();

  const initDateSelectorValue = () => {
    const dateSelectorValue = searchParams.get(
      'dateSelectorValue',
    ) as TDateSelectValues;
    if (
      FeatureSwitches.enableLSSCPS25MonthsDefault === true &&
      customerType === 'b2b'
    ) {
      return 'last25Months';
    }
    if (dateSelectorValue) {
      return dateSelectorValue;
    }
    if (customerType === 'b2b') {
      return 'last6Months';
    }
    return 'recentOrders';
  };

  // Hook used for opening add receipt drawer anywhere on purchase history
  const [openAddReceiptDrawer, setOpenAddReceiptDrawer] = useState(false);
  // Hook used for alert message on purchase history page
  const [alertState, setAlertState] = useState<IAlert | null>(null);
  // Hook used for local delivery alert message on purchase history page
  const [localDeliveryAlertState, setLocalDeliveryAlertState] =
    useState<IAlert | null>(null);
  // Hook used for "View History From" dropdown
  const [dateSelectorValue, setDateSelectorValue] = useState<TDateSelectValues>(
    // eslint-disable-next-line no-nested-ternary
    (searchParams.get('dateSelectorValue') as TDateSelectValues) ||
      initDateSelectorValue(),
  );

  // Hook used for storing order details information for each order to be called back
  const [allOrderDetails, setAllOrderDetails] = useState<
    IOrderDetailsResponse[] | []
  >([]);
  const [viewType, setViewType] = useState<string>('');
  const [localDeliveryInfo, setLocalDeliveryInfo] = useState({
    localDeliveryData: undefined,
    localDeliveryError: undefined,
    localDeliveryLoaded: false,
  });

  const { isMobile } = useCommonContext();

  const viewTypeStorageKey = getAccountScopedStorageKey(VIEW_TYPE_STORAGE_KEY);
  const localStorageViewType: string | null | undefined = viewTypeStorageKey
    ? getWindow()?.localStorage.getItem(viewTypeStorageKey)
    : undefined;

  useEffect(() => {
    const initialViewType = () => {
      if (isMobile) return 'Card';
      if (localStorageViewType) return localStorageViewType;
      if (customerType !== 'b2b') return 'Card';
      return 'Table';
    };

    setViewType(initialViewType);
  }, [localStorageViewType]);

  // Utility to check to see if the user can select purchases filter.
  // NOTE: on first render rules is [] (async), so for B2B this always resolves
  // to 'myOrders'. The effect below corrects it once policies have loaded.
  const initResultsFilter =
    rulesLoaded &&
    isUserAllowedToViewAllPurchases(customerRole, customerType, rules)
      ? 'allOrders'
      : 'myOrders';

  const sessionPHFilter = getFilterDetailsFromSession();

  // For B2B, always initialize to 'myOrders' (safe default) — policies haven't
  // loaded yet so session-stored 'allOrders' could be over-permissive.
  // For non-B2B, session-stored userView is safe to apply immediately.
  const safeInitFilter: 'allOrders' | 'myOrders' =
    customerType === 'b2b'
      ? 'myOrders'
      : sessionPHFilter?.userView ?? initResultsFilter;

  const [pHRequest, setPHRequest] = useState(
    initPHRequestBuilder({
      customerType,
      customerRole,
      dateSelectorValue: sessionPHFilter?.selectedDate ?? dateSelectorValue,
      searchParams,
      svocID,
      initResultsFilter: safeInitFilter,
      sessionData: sessionPHFilter,
      timezone,
    }),
  );

  // For non-B2B, ready immediately. For B2B, hold until rulesLoaded so that
  // GetOrderHistoryData fires exactly once with the permission-correct resultsFilter.
  const [phRequestReady, setPhRequestReady] = useState(customerType !== 'b2b');

  // Once B2B policies have loaded, recompute the allowed filter and flip phRequestReady.
  // React 18 batches both setState calls into one render, so GetOrderHistoryData
  // sees the correct resultsFilter and enabled=true at the same time — one API call.
  useEffect(() => {
    if (!rulesLoaded || customerType !== 'b2b') return;
    const allowedFilter: 'allOrders' | 'myOrders' =
      isUserAllowedToViewAllPurchases(customerRole, customerType, rules)
        ? 'allOrders'
        : 'myOrders';
    const latestSessionFilter = getFilterDetailsFromSession();
    // Only honour a session-stored 'allOrders' if the policy permits it.
    const effectiveFilter: 'allOrders' | 'myOrders' =
      latestSessionFilter?.userView === 'allOrders' &&
      allowedFilter === 'allOrders'
        ? 'allOrders'
        : allowedFilter;
    setPHRequest((prev) => ({ ...prev, resultsFilter: effectiveFilter }));
    setPhRequestReady(true);
  }, [rulesLoaded, rules, customerType, customerRole]);

  // Hook for getting order/purchase history data
  const {
    orderHistoryData,
    orderHistoryError,
    orderHistoryLoaded,
    cpsDataSource,
  } = GetOrderHistoryData(pHRequest, phRequestReady);

  const [LDRequest, setLDRequest] = useState<ILDRequest>(
    initLDRequestBuilder({
      dateSelectorValue: 'last6Months',
      searchParams,
      svocID,
      searchType: 'ORDERS',
      resultsFilter: 'allOrders',
      sortBy: 'salesDate',
      sortOrder: 'DESC',
      groupBy: 'FULLFILLMENT_TYPE',
    }),
  );

  const [phFilterRequest, setPHFilterRequest] = useState<IPHFilterRequest>({
    startDate: pHRequest.startDate,
    endDate: pHRequest.endDate,
    filterTypes: ['payment', 'poJobName', 'userId'],
    customerAccountId: svocID,
  });
  const [hasOpenedFilterDrawer, setHasOpenedFilterDrawer] = useState(false);

  const triggerPHFilterFetch = React.useCallback(() => {
    setHasOpenedFilterDrawer(true);
  }, []);

  // update filter call if date range changes
  useEffect(() => {
    setPHFilterRequest((prev) => ({
      ...prev,
      startDate: pHRequest.startDate,
      endDate: pHRequest.endDate,
    }));
  }, [pHRequest.startDate, pHRequest.endDate]);

  // hook for getting search filter data
  const {
    isLoading: isPHFilterLoading,
    cpsFilterError,
    getCPSFiltersResponse,
  } = GetCPSFilters(
    phFilterRequest,
    Boolean(
      (FeatureSwitches.enablePOJOBAndPaymentSearch ||
        FeatureSwitches.enableTotalAmountAndUserCustomFilters) &&
        hasOpenedFilterDrawer,
    ),
  );

  const contextValue = useMemo<IPurchaseHistoryContext>(
    () => ({
      alertState,
      localDeliveryAlertState,
      dateSelectorValue,
      initResultsFilter,
      openAddReceiptDrawer,
      orderHistoryData,
      orderHistoryError,
      orderHistoryLoaded,
      localDeliveryData: localDeliveryInfo.localDeliveryData,
      localDeliveryError: localDeliveryInfo.localDeliveryError,
      localDeliveryLoaded: localDeliveryInfo.localDeliveryLoaded,
      setLocalDeliveryInfo,
      pHRequest,
      lDRequest: LDRequest,
      setAlertState,
      allOrderDetails,
      setAllOrderDetails,
      setLocalDeliveryAlertState,
      setDateSelectorValue,
      setOpenAddReceiptDrawer,
      setPHRequest,
      setLDRequest,
      setViewType,
      viewType,
      customerType,
      customerRole,
      cpsDataSource,
      rules,
      isPHFilterLoading,
      cpsFilterError,
      setPHFilterRequest,
      triggerPHFilterFetch,
      poJobFilters:
        getCPSFiltersResponse?.orderHistoryFilterResponse?.poJobName || [],
      paymentFilters:
        getCPSFiltersResponse?.orderHistoryFilterResponse?.payment || [],
      userFilters:
        getCPSFiltersResponse?.orderHistoryFilterResponse?.userId || [],
    }),
    [
      alertState,
      localDeliveryAlertState,
      dateSelectorValue,
      initResultsFilter,
      openAddReceiptDrawer,
      orderHistoryData,
      orderHistoryError,
      orderHistoryLoaded,
      localDeliveryInfo.localDeliveryData,
      localDeliveryInfo.localDeliveryError,
      localDeliveryInfo.localDeliveryLoaded,
      setLocalDeliveryInfo,
      pHRequest,
      LDRequest,
      setAlertState,
      allOrderDetails,
      setAllOrderDetails,
      setLocalDeliveryAlertState,
      setDateSelectorValue,
      setOpenAddReceiptDrawer,
      setPHRequest,
      setLDRequest,
      setViewType,
      viewType,
      customerType,
      customerRole,
      cpsDataSource,
      rules,
      isPHFilterLoading,
      cpsFilterError,
      setPHFilterRequest,
      triggerPHFilterFetch,
      getCPSFiltersResponse?.orderHistoryFilterResponse?.poJobName,
      getCPSFiltersResponse?.orderHistoryFilterResponse?.payment,
      getCPSFiltersResponse?.orderHistoryFilterResponse?.userId,
    ],
  );

  return (
    <PurchaseHistoryContext.Provider value={contextValue}>
      {children}
    </PurchaseHistoryContext.Provider>
  );
};

// Named export at end for hooks (separating from Provider definition)
export { PurchaseHistoryContext };
